import React from "react";
import {
  Wallet,
  FileText,
  Scale,
  ArrowDownUp,
  Calculator,
  Percent,
  Sprout,
  SprayCan,
  BarChart2,
  CircleDollarSign,
  Leaf,
  Package,
  Banknote,
  Truck,
  PlusCircle,
  MinusCircle,
} from "lucide-react";

interface BoxComponentProps {
  title: string;
  id: number;
  value: any;
  colorProp?: any;
  amount?: any;
}

const BoxComponent: React.FC<BoxComponentProps> = ({
  title,
  id,
  value,
  colorProp,
  amount,
}) => {
  const renderIcon = () => {
    switch (id) {
      case 1: // مانده حساب پیمانکار
        return (
          <div className="flex items-center">
            {value > 0 ? (
              <PlusCircle className="w-9 h-9 text-green-500" />
            ) : value < 0 ? (
              <MinusCircle className="w-9 h-9 text-red-500" />
            ) : (
              <Wallet className="w-9 h-9 text-blue-500" />
            )}
          </div>
        );
      case 2: // قراردادها - FileText
        return <FileText className="w-9 h-9 text-red-500" />;
      case 3: // تناژ تحویلی - Scale
        return <Scale className="w-9 h-9 text-green-500" />;
      case 4: // بدهی به کشاورزان - ArrowDownUp (پایین)
        return (
          <ArrowDownUp
            className={`w-9 h-9 text-red-600 ${getStatusColor(value).icon}`}
          />
        );
      case 5: // طلب از کشاورزان - ArrowDownUp (بالا)
        return <ArrowDownUp className="w-9 h-9 text-purple-500" />;
      case 6: // مانده تا تسویه
        return (
          <div className="flex items-center">
            {value > 0 ? (
              <PlusCircle className="w-9 h-9 text-green-500" />
            ) : value < 0 ? (
              <MinusCircle className="w-9 h-9 text-red-500" />
            ) : (
              <Calculator className="w-9 h-9 text-yellow-500" />
            )}
          </div>
        );
      case 7: // کارمزد پیمانکار
        return (
          <div className="flex items-center">
            {value > 0 ? (
              <PlusCircle className="w-9 h-9 text-green-500" />
            ) : value < 0 ? (
              <MinusCircle className="w-9 h-9 text-red-500" />
            ) : (
              <Percent className="w-9 h-9 text-pink-500" />
            )}
          </div>
        );
      case 8: // سود از بذر - Sprout
        return <Sprout className="w-9 h-9 text-green-400" />;
      case 9: // سود از سم - SprayCan
        return <SprayCan className="w-9 h-9 text-red-600" />;
      case 10: // وضعیت کلی - BarChart2
        return (
          <BarChart2 className={`w-9 h-9 ${getStatusColor(value).icon}`} />
        );
      case 11: // سود کشاورزی - CircleDollarSign
        return <CircleDollarSign className="w-9 h-9 text-green-500" />;
      case 12: // هزینه بذر - Sprout
        return <Sprout className="w-9 h-9 text-brown-500" />;
      case 13: // هزینه سم - SprayCan
        return <SprayCan className="w-9 h-9 text-red-500" />;
      case 14: // جمع پرداختی‌ها - Wallet
        return <Wallet className="w-9 h-9 text-blue-500" />;
      case 15: // تعداد بارهای تحویلی - Truck
        return <Truck className="w-9 h-9 text-orange-500" />;
      case 16: // وزن کل تحویلی - Scale
        return <Scale className="w-9 h-9 text-purple-500" />;
      case 17: // پرداخت خالص - Banknote
        return <Banknote className="w-9 h-9 text-cyan-500" />;
      case 18: // کسر کارمزد - Percent
        return <Percent className="w-9 h-9 text-pink-500" />;
      case 19: // سهمیه شکر - Package
        return <Package className="w-9 h-9 text-yellow-500" />;
      case 20: // سهمیه تفاله - Leaf
        return <Leaf className="w-9 h-9 text-gray-500" />;
         case 21: // سهمیه تفاله - Leaf
        return <Leaf className="w-9 h-9 text-gray-500" />;
      default: // پیش‌فرض - CircleDollarSign
        return <CircleDollarSign className="w-9 h-9 text-gray-500" />;
    }
  };

  // تابع برای تعیین رنگ بر اساس مقدار وضعیت
  const getStatusColor = (val: number) => {
    if (val > 0) {
      return { text: "text-green-600 font-bold", icon: "text-green-600" };
    } else if (val < 0) {
      return { text: "text-red-600 font-bold", icon: "text-red-600" };
    }
    return { text: "", icon: "text-indigo-500" };
  };

  // برای وضعیت کلی استایل خاص اعمال می‌شود
  const isStatusItem = id === 10;
  const statusColor = isStatusItem ? getStatusColor(value).text : "";
  const formattedValue = value?.toLocaleString("fa-IR"); // تبدیل عدد به فرمت فارسی

  return (
    <div
      className="
        bg-white dark:bg-gray-700 
        flex flex-col justify-between 
        rounded-lg w-full h-24 
        shadow-md dark:shadow-gray-900 
        p-3 
        border border-gray-200 dark:border-gray-600 
        hover:border-gray-300 dark:hover:border-gray-500 
        transition-colors duration-200
      "
    >
      <div className="text-right text-sm text-gray-600 dark:text-gray-300 font-medium">
        {title}
      </div>
      <div className="flex justify-between items-end">
        <div
          className={`text-left text-xl ${
            colorProp ||
            (isStatusItem ? statusColor : title === "سود شما از کشاورزی" ? "text-green-600" : "text-gray-900 dark:text-gray-100")
          } font-bold`}
        >
          {formattedValue}
          {!title.includes("تعداد") && !title.includes("تناژ") && (
            <span className="text-xs mx-1 text-blue-600 dark:text-gray-100">
              {amount ? amount : "تومان"}
            </span>
          )}
        </div>
        <div className="p-2 bg-transparent rounded-lg">{renderIcon()}</div>
      </div>
    </div>
  );
};

export default BoxComponent;
